/* eslint-disable jsx-a11y/img-redundant-alt */
import CustomDatePicker from '@/components/datePicker';
import HelpCenter from '@/components/HelpCenter';
import Layout from '@/components/layout';
import { Enum } from '@/constants';
import { dateToTimeStamp } from '@/helpers';
import userPlans from '@/hooks/userPlans';
import { apiCaller } from '@/redux/query';
import slice from '@/redux/slice';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import blockedVisitorSlice, { blockedDetailSelector, blockHistorySelector } from '@/redux/slice/blockedVisitor.slice';
import { dataSettingsSelector } from '@/redux/slice/dataSettings.slice';
import visitorLogSlice, {
  allAccessSelector,
  perPageSelector,
  tabsTableSelector,
  titleBtnDatePickerSelector,
  visitorDetailSelector,
  visitorLogSelector,
} from '@/redux/slice/visitorAnalytics.slice';
import { Badge, Button } from '@shopify/polaris';
import { RefreshIcon } from '@shopify/polaris-icons';
import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import Analytics from './components/Analytics';
import AnalyticsChart from './components/Chart';
import HeatMap from './components/heatMap';
import TableAnalytics from './components/table';
import TopFiveList from './components/TopFiveList';
import { AnalyticsStyled } from './styled';

const SecondaryAction = (): JSX.Element => {
  const dispatch = useDispatch();
  const { userPlanFree } = userPlans();
  const perPage = useSelector(perPageSelector);
  const allAccess = useSelector(allAccessSelector);
  const blockHistoryTable = useSelector(blockHistorySelector);
  const visitorLogTable = useSelector(visitorLogSelector);
  const blockedDetailTable = useSelector(blockedDetailSelector);
  const visitorsDetailTable = useSelector(visitorDetailSelector);
  const titleBtn = useSelector(titleBtnDatePickerSelector);
  const selected = useSelector(tabsTableSelector);
  const isSkip = useSelector(isSkipApiSelector);
  const { t } = useTranslation(['visitor_analytics']);
  const fetchVisitor = apiCaller.useFetchVisitorListQuery(
    {
      ...visitorLogTable,
      sort: visitorLogTable.sort.toUpperCase(),
      endDate: dateToTimeStamp(allAccess.endDate),
      startDate: dateToTimeStamp(allAccess.startDate),
      perPage,
    },
    { skip: selected === 1 || isSkip },
  );
  const fetchBlock = apiCaller.useFetchVisitorBlockListQuery(
    {
      ...blockHistoryTable,
      sort: blockHistoryTable.sort.toUpperCase(),
      endDate: dateToTimeStamp(allAccess.endDate),
      startDate: dateToTimeStamp(allAccess.startDate),
      perPage,
    },
    { skip: selected === 0 || isSkip },
  );
  const fetchChartAnalytics = apiCaller.useFetchChartAnalyticsQuery(
    {
      endDate: dateToTimeStamp(allAccess.endDate),
      startDate: dateToTimeStamp(allAccess.startDate),
    },
    { skip: selected === 1 || isSkip },
  );
  const fetchChartDashboard = apiCaller.useFetchChartBlockQuery(
    {
      endDate: dateToTimeStamp(allAccess.endDate),
      startDate: dateToTimeStamp(allAccess.startDate),
    },
    { skip: selected === 0 || isSkip },
  );
  const handleRefreshClick = async () => {
    if (selected === 0) {
      await Promise.all([fetchVisitor.refetch(), fetchChartAnalytics.refetch()]);
    } else if (selected === 1) {
      await Promise.all([fetchBlock.refetch(), fetchChartDashboard.refetch()]);
    }
  };
  const onSaveDatePicker = (startDate: Date, endDate: Date) => {
    // AllAccess
    dispatch(slice.visitorAnalyticsSlice.actions.handleAllAccessTable({ ...visitorLogTable, startDate, endDate }));
    // Blocked
    dispatch(slice.blockedVisitorSlice.actions.handleBlockedDetail({ ...blockedDetailTable, startDate, endDate }));
    dispatch(
      blockedVisitorSlice.actions.handleBlockHistoryTable({
        ...blockHistoryTable,
        page: 1,
      }),
    );
    // Allow
    dispatch(slice.visitorAnalyticsSlice.actions.handleVisitorsDetailTable({ ...visitorsDetailTable, startDate, endDate }));
    dispatch(
      visitorLogSlice.actions.handleVisitorLogTable({
        ...visitorLogTable,
        page: 1,
      }),
    );
  };
  const onSaveTitleBtnDatePicker = (title: string) => {
    dispatch(slice.visitorAnalyticsSlice.actions.handleTitleBtnDatePicker(title));
    dispatch(slice.visitorAnalyticsSlice.actions.handleTitleBtnDatePickerVisitorsDetail(title));
    dispatch(slice.blockedVisitorSlice.actions.handleTitleBtnDatePickerBlockedDetail(title));
  };

  return (
    <div className="d-flex">
      <Button
        onClick={handleRefreshClick}
        loading={fetchVisitor.isLoading && fetchChartAnalytics.isLoading && fetchBlock.isLoading && fetchChartDashboard.isLoading}
        icon={RefreshIcon}
      >
        {t('visitor_analytics:refresh')}
      </Button>
      <div className="ml-8">
        <CustomDatePicker
          titleButton={titleBtn}
          setTitleButton={onSaveTitleBtnDatePicker}
          startDate={allAccess.startDate}
          endDate={allAccess.endDate}
          onSave={onSaveDatePicker}
          isShowSelectedTime={true}
          disabled={userPlanFree}
          conditions
        />
      </div>
    </div>
  );
};

function AnalyticsPage() {
  // const navigate = useNavigate();
  // const dispatch = useDispatch();
  const { t } = useTranslation(['visitor_analytics', 'common']);
  // const { userPlanFree } = userPlans();
  // const banner = useSelector(bannerSelector);
  const dataSettings = useSelector(dataSettingsSelector);
  // const [handleAnalytic, handleAnalyticStatus] = apiCaller.useHandleAnalyticStatusMutation();
  // const handleChangeStatusAnalytics = () => {
  //   handleAnalytic(!dataSettings?.settings.enableVisitorAnalytics);
  // };

  return (
    <Layout
      layoutProps={{
        title: t('visitor_analytics:visitor_analytics'),
        fullWidth: true,
        primaryAction:
          dataSettings && dataSettings.settings.user.plan !== Enum.UserPlan.FREE ? (
            <div className="d-flex">
              <HelpCenter />
              {/* <div className="ml-8">
                <ButtonSupport />
              </div> */}
            </div>
          ) : (
            <></>
            // <Button
            //   onClick={handleChangeStatusAnalytics}
            //   variant="primary"
            //   tone={dataSettings && dataSettings?.settings?.enableVisitorAnalytics ? 'critical' : 'success'}
            //   loading={handleAnalyticStatus.isLoading}
            // >
            //   {dataSettings && dataSettings.settings.enableVisitorAnalytics
            //     ? t('visitor_analytics:deactivate_visitor_analytics')
            //     : t('visitor_analytics:activate_visitor_analytics')}
            // </Button>
          ),
        titleMetadata: <Badge tone="info-strong">Coming soon</Badge>,
        // dataSettings && dataSettings.settings.user.plan === Enum.UserPlan.FREE ? (
        //   dataSettings?.settings?.enableVisitorAnalytics ? (
        //     <Badge tone="success">{t('visitor_analytics:tracking')}</Badge>
        //   ) : (
        //     // <Badge tone="enabled">{t('visitor_analytics:not_track')}</Badge>
        //     <Badge tone="enabled">Coming soon</Badge>
        //   )
        // ) : null,
      }}
    >
      <AnalyticsStyled>
        {/* <>
          {dataSettings && !dataSettings.settings.enableVisitorAnalytics ? (
            <>
              <Collapsible
                id="banner-warning-demo-data"
                open={banner.bannerWarningDemoDataVA}
                transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
              >
                <div className="mb-16 banner-warning-demo-data">
                  <Banner
                    tone="warning"
                    onDismiss={() => {
                      dispatch(
                        handleVisibleBanner({
                          key: 'bannerWarningDemoDataVA',
                          value: false,
                        }),
                      );
                    }}
                  >
                    {t('visitor_analytics:visitor_analytics_demo_deactivated')}{' '}
                    <Link onClick={() => navigate(PATH.PRICING_PLAN)}>
                      {t('visitor_analytics:unlock_full_visitor_analytics')}
                    </Link>
                  </Banner>
                </div>
              </Collapsible>
            </>
          ) : (
            <>
              {banner.contactSupportFromVA && userPlanFree ? (
                <div className="mb-16">
                  <Banner
                    tone="info"
                    onDismiss={() => {
                      dispatch(
                        handleVisibleBanner({
                          key: 'contactSupportFromVA',
                          value: false,
                        }),
                      );
                    }}
                    action={{
                      content: t('common:learn_more'),
                      onAction: () => {
                        window.open('https://docs.ipblocker.io/getting-started/visitor-analytics', '_blank');
                      },
                    }}
                  >
                    {t('visitor_analytics:blocked_visitors_reports')}.
                  </Banner>
                </div>
              ) : null}
            </>
          )}
        </> */}
        <div className="mb-16">
          <SecondaryAction />
        </div>
        <Analytics />
        <div className="mt-16">
          <AnalyticsChart />
        </div>
        <div className="mt-16">
          <HeatMap />
        </div>
        <div className="mt-16">
          <TopFiveList />
        </div>
        <div className="mt-16">
          <TableAnalytics />
        </div>
      </AnalyticsStyled>
    </Layout>
  );
}

export default memo(AnalyticsPage);
